
import { useStore } from '@/store'
import { ThreeActionTypes } from '@/store/modules/three/action-types'
import { defineComponent, inject, onDeactivated, onMounted, onUnmounted } from 'vue'
import { ref, computed, watch } from 'vue'
import RenderService from '@/services/Three/render.service'
import ISceneModel, { SceneModel } from '@/models/scene-model'
import settings from '@/settings/settings.json'
import References from '@/singletons/references'
import * as UrlConstants from '@/constants/url-constants'
import SceneService from '@/services/Three/scene.service'
import SentrySingleton from '@/singletons/sentry-singleton'
import WebglCompatibilityService from '@/services/Three/webgl-compatibility.service'
import StageDeterminerService from '@/services/page/stage-determiner.service'
import * as THREE from 'three'

export default defineComponent({
  name: 'ThreeScene',
  setup() {
    const store = useStore()
    let sceneContainerRef = ref()
    let canvasRef = ref()
    let stageDeterminer = new StageDeterminerService()
    const isControlsEnabled = computed(() => store.getters.isControlsEnabled)
    const isThreeMode = computed(() => store.getters.isThreeMode)
    const emitter: any = inject('emitter')
    const isRenderer = inject('isRenderer') as boolean
    const isEvent = inject('isEvent') as boolean
    let renderService: RenderService
    let sceneService: SceneService
    let postprocessing = true
    let isInit = false

    onMounted(() => {
      try {
        sceneService = new SceneService()
        if (isThreeMode.value && !isInit) {
          init()
        }
        window.addEventListener('resize', () => sceneService.onWindowResize())
      } catch (err) {
        console.log("isWebGLAvailable: " + WebglCompatibilityService.isWebGLAvailable)
        SentrySingleton.instance.captureException(err)
      }
    })

    function init() {
      THREE.Cache.enabled = true;
      isInit = true
      postprocessing = determinePostprocessing()
      const sceneContainer = sceneContainerRef.value as HTMLCanvasElement
      const canvas = canvasRef.value as HTMLCanvasElement
      renderService = new RenderService()
      const alpha = isRenderer || isEvent

      const scene = sceneService.createScene()
      const renderer = sceneService.createRenderer(canvas, sceneContainer, postprocessing, alpha)
      const camera = sceneService.createCamera(sceneContainer)
      const controls = sceneService.createControls(camera, sceneContainer, isControlsEnabled.value)
      sceneService.createLights(scene)
      const composer = sceneService.createComposer(scene, camera, renderer, postprocessing)

      let sceneModel = new SceneModel({
        scene,
        camera,
        renderer,
        composer,
        fxaaPass: postprocessing ? sceneService.createFxaaPass(composer!, sceneContainer) : null,
        fisheyePass: postprocessing ? sceneService.createFisheyePass(composer!, camera) : null,
        gammaCorrectionPass: postprocessing ? sceneService.createGammaCorrectionPass(composer!, postprocessing) : null,
        controls,
        canvas,
        sceneContainer,
        maxAnisotropy: renderer.capabilities.getMaxAnisotropy(),
        postprocessing
      })
      References.instance.setSceneReferences(sceneModel)
      store.dispatch(ThreeActionTypes.SET_SCENE_LOADED, true)
      setupAnimationLoop(sceneModel)
    }

    // prettier-ignore
    watch(() => isControlsEnabled.value, (enabled) => {
      try {
        References.instance.getSceneReferences().controls.enabled = enabled;
        const urlParams = new URLSearchParams(window.location.search);
        const freeCam = urlParams.has(UrlConstants.freeCamera) && urlParams.get(UrlConstants.freeCamera) == 'true'
        if (!freeCam && !window.location.pathname.includes('renderer.html') && stageDeterminer.isProd()) {
          References.instance.getSceneReferences().controls.maxDistance = 1.8;
          References.instance.getSceneReferences().controls.minDistance = 0.7;
        }
      }
      catch(err) {
        SentrySingleton.instance.captureException(err)
      }
    })

    function determinePostprocessing() {
      const urlParams = new URLSearchParams(window.location.search)
      if (urlParams.has(UrlConstants.postprocessing) && urlParams.get(UrlConstants.postprocessing) == 'true') {
        return true
      }
      if (urlParams.has(UrlConstants.postprocessing) && urlParams.get(UrlConstants.postprocessing) == 'false') {
        return false
      }
      return postprocessing
    }

    function setupAnimationLoop(sceneModel: ISceneModel) {
      try {
        const showFPS = determineShowFps()
        let stats: any
        if (showFPS) {
          stats = sceneService.createFPS()
        }

        sceneModel.renderer.setAnimationLoop(() => {
          if (showFPS) {
            stats.begin()
          }
          if(sceneModel.controls) {
            sceneModel.controls.update()
          }
          renderService.render(sceneModel)
          emitter.emit('update')

          if (showFPS) {
            stats.end()
          }
        })
      } catch (err) {
        SentrySingleton.instance.captureException(err)
      }
    }

    function determineShowFps() {
      const urlParams = new URLSearchParams(window.location.search)
      const showFps = urlParams.has(UrlConstants.showfps) && urlParams.get(UrlConstants.showfps) == 'true'
      return showFps || settings.showFPS
    }

    onUnmounted(() => {
      try {
        References.instance.getSceneReferences().renderer.dispose()
        sceneService.cleanScene(References.instance.getSceneReferences().scene)
        window.removeEventListener('resize', sceneService.onWindowResize)
        store.dispatch(ThreeActionTypes.THREE_RESET, false)
      } catch (err) {
        SentrySingleton.instance.captureException(err)
      }
    })

    return {
      isThreeMode,
      sceneContainerRef,
      canvasRef,
      isControlsEnabled
    }
  }
})
